import { units } from '@ha/dsl';
import React from 'react';
import DayPicker, {
  DayPickerProps,
  DayModifiers,
  CaptionElementProps,
  RangeModifier,
} from 'react-day-picker';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { DayJSLocaleUtils } from '@ha/date';
import { DateRangePickerProps } from 'ha/components/next/DatePicker/types';
import { usePrevious } from 'ha/helpers/hooks/usePrevious';

import Caption from './Calendar/Caption';
import NavBar from './Calendar/NavBar';
import WeekDay from './Calendar/WeekDay';
import Day from './Calendar/Day';

const useStyles = makeStyles((theme: Theme) => ({
  day: {
    fontSize: units.rem(1),
    color: theme.palette.grey[700],
    fontWeight: theme.typography.fontWeightMedium,
    padding: 0,
    border: '2px solid transparent',
    transition: 'background-color 0.5s ease',
    '&.start': {
      color: 'white',
      borderBottomLeftRadius: '50%',
      borderTopLeftRadius: '50%',
      '& > button': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    '&.end': {
      color: 'white',
      borderBottomRightRadius: '50%',
      borderTopRightRadius: '50%',
      '& > button': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    '&.disabled': {
      pointerEvents: 'none',
      color: theme.palette.grey[300],
    },
    '&.blocked': {
      color: theme.palette.grey[400],
      borderRadius: '50%',
      '& > button': {
        backgroundColor: theme.palette.grey[300],
        background: `linear-gradient(-45deg, transparent 0%, transparent 48%, ${theme.palette.grey[400]} 49%, ${theme.palette.grey[400]} 50%, transparent 51%, transparent 100%)`,
      },
      '&.start': {
        color: 'white',
      },
      '&.end': {
        color: 'white',
      },
    },
    '&:first-child': {
      borderBottomLeftRadius: '50%',
      borderTopLeftRadius: '50%',
    },
    '&:last-child': {
      borderBottomRightRadius: '50%',
      borderTopRightRadius: '50%',
    },
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  selected: {
    backgroundColor: theme.palette.secondary.light,
    fontWeight: theme.typography.fontWeightMedium,
    '&.blocked': {
      borderRadius: 0,
      '& > button': {
        background: `linear-gradient(-45deg, transparent 0%, transparent 48%, ${theme.palette.grey[400]} 49%, ${theme.palette.grey[400]} 50%, transparent 51%, transparent 100%)`,
      },
      '&.start': {
        borderBottomLeftRadius: '50%',
        borderTopLeftRadius: '50%',
        '& > button': {
          backgroundColor: theme.palette.secondary.dark,
        },
      },
      '&.end': {
        borderBottomRightRadius: '50%',
        borderTopRightRadius: '50%',
        '& > button': {
          backgroundColor: theme.palette.secondary.dark,
        },
      },
      '&:first-child': {
        borderBottomLeftRadius: '50%',
        borderTopLeftRadius: '50%',
      },
      '&:last-child': {
        borderBottomRightRadius: '50%',
        borderTopRightRadius: '50%',
      },
    },
  },
  weekdays: {},
  months: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      placeItems: 'flex-start',
    },
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  month: {
    [theme.breakpoints.up('sm')]: {
      minWidth: '300px',
    },
  },
  caption: {},
  weekdaysRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  week: {
    display: 'flex',
    justifyContent: 'center',
    margin: '2px 0',
  },
  dayLabel: {
    width: 36,
    textAlign: 'center',
    color: theme.palette.text.hint,
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  weekLabel: {
    width: 36,
    textAlign: 'center',
    color: theme.palette.grey[700],
  },
  outside: {
    backgroundColor: 'inherit!important',
    height: 40,
    width: 40,
  },
  iconButton: {
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  wrapper: {
    position: 'relative',
    outline: 'none',
  },
  interactionDisabled: {
    display: 'inline-block',
  },
  weekday: {},
  weekNumber: {},
  today: {},
  body: {
    minHeight: '230px',
  },
  disabled: {},
  container: {
    maxWidth: '280px',
    margin: '0 auto',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'none',
    },
  },
  navBar: {},
  navButtonPrev: {},
  navButtonNext: {},
  footer: {},
  todayButton: {},
  navButtonInteractionDisabled: {},
}));

interface OwnProps {
  CaptionSubtitle?: DateRangePickerProps['CaptionSubtitle'];
  classes?: { [key: string]: string };
  highlightMoveInWindows?: boolean;
  moveInWindows?: RangeModifier[];
  moveInWindowsTooltip: string;
  usePrevMonth?: boolean;
  customTooltip?: React.ElementType<{ day: Date }>;
}

export type Props = OwnProps & DayPickerProps;

const Calendar: React.FC<Props> = props => {
  const {
    CaptionSubtitle,
    classes: customClasses = {},
    highlightMoveInWindows = false,
    moveInWindows,
    moveInWindowsTooltip,
    month,
    usePrevMonth = false,
    customTooltip,
  } = props;
  const classes = useStyles();
  const prevMonth = usePrevious(month);

  const CaptionElement = React.memo((captionProps: CaptionElementProps) => (
    <Caption {...captionProps}>{CaptionSubtitle}</Caption>
  ));

  return (
    <DayPicker
      containerProps={{ tabIndex: -1 }}
      navbarElement={NavBar}
      weekdayElement={WeekDay}
      captionElement={CaptionElement}
      renderDay={(day: Date, modifiers: DayModifiers) => (
        <Day
          day={day}
          highlightMoveInWindows={highlightMoveInWindows}
          modifiers={modifiers}
          moveInWindows={moveInWindows}
          moveInWindowsTooltip={moveInWindowsTooltip}
          customTooltip={customTooltip}
        />
      )}
      {...props}
      classNames={{
        ...classes,
        ...customClasses,
      }}
      month={usePrevMonth ? prevMonth : month}
      localeUtils={DayJSLocaleUtils}
    />
  );
};

export default Calendar;
